/**
 * PrintHistoryModal - Modal de Histórico de Impressões com Visualização de Medicamentos
 * Mostra todas as impressões de uma receita com snapshot completo dos medicamentos
 */

import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Clock, 
  Printer, 
  User, 
  Calendar, 
  ArrowRight, 
  Pill, 
  ChevronDown, 
  ChevronUp,
  FileText,
  Eye
} from 'lucide-react';

export default function PrintHistoryModal({ open, onClose, prescriptionId, prescriptionName }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState({});

  useEffect(() => {
    if (open && prescriptionId) {
      fetchHistory();
    }
  }, [open, prescriptionId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/prescriptions/${prescriptionId}/print-history`);
      setHistory(response.data);
      // Expandir automaticamente o primeiro registro
      if (response.data.length > 0) {
        setExpandedEntries({ [response.data[0].id]: true });
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (entryId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Renderizar medicamento formatado
  const renderMedication = (med, index) => {
    const dosageInfo = [
      med.name,
      med.dosage ? `${med.dosage}${med.dosage_unit || 'MG'}` : ''
    ].filter(Boolean).join(' ');

    return (
      <div 
        key={index}
        className="p-3 bg-white rounded-lg border border-gray-100 hover:border-primary/30 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-xs font-bold text-primary">{index + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">
              {dosageInfo || 'Medicamento não especificado'}
            </p>
            {med.instructions && (
              <p className="text-sm text-gray-600 mt-1">
                {med.instructions}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {med.quantity && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Qtd: {med.quantity}
                </Badge>
              )}
              {med.via && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  {med.via}
                </Badge>
              )}
              {med.packaging_type === 'uso_continuo' && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                  Uso Contínuo
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Histórico de Impressões
          </DialogTitle>
          <DialogDescription>
            {prescriptionName || 'Receita'} - {history.length} impressão(ões) registrada(s)
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            Carregando histórico...
          </div>
        ) : history.length === 0 ? (
          <div className="py-12 text-center">
            <Printer className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-muted-foreground">Nenhuma impressão registrada ainda.</p>
            <p className="text-sm text-gray-400 mt-2">
              O histórico será registrado automaticamente ao imprimir a receita.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((entry, index) => (
              <div
                key={entry.id}
                className={`rounded-xl border overflow-hidden ${
                  index === 0 
                    ? 'bg-primary/5 border-primary/20' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                {/* Header do registro */}
                <div 
                  className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                  onClick={() => toggleExpand(entry.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        index === 0 ? 'bg-primary/20' : 'bg-gray-200'
                      }`}>
                        <Printer className={`w-5 h-5 ${index === 0 ? 'text-primary' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {index === 0 ? 'Última Impressão' : `Impressão #${history.length - index}`}
                          </span>
                          {index === 0 && (
                            <Badge className="bg-primary text-white text-xs">
                              Mais recente
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {formatDate(entry.printed_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        <Pill className="w-3 h-3 mr-1" />
                        {entry.medications_snapshot?.length || 0} medicamento(s)
                      </Badge>
                      {expandedEntries[entry.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Info resumida */}
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>Por:</span>
                      <span className="font-medium text-gray-900">
                        {entry.printed_by || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>Validade:</span>
                      {entry.previous_expiry && (
                        <>
                          <span className="text-gray-400 line-through text-xs">
                            {formatDateShort(entry.previous_expiry)}
                          </span>
                          <ArrowRight className="w-3 h-3 text-gray-400" />
                        </>
                      )}
                      <span className="font-medium text-green-600">
                        {formatDateShort(entry.new_expiry)}
                      </span>
                    </div>

                    {entry.uso && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{entry.uso}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lista de medicamentos (expandível) */}
                {expandedEntries[entry.id] && entry.medications_snapshot && (
                  <div className="border-t border-gray-200 p-4 bg-white/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-primary" />
                      <h4 className="font-semibold text-gray-700">
                        Medicamentos no momento da impressão
                      </h4>
                    </div>
                    
                    {entry.medications_snapshot.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        Nenhum medicamento registrado nesta impressão
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {entry.medications_snapshot.map((med, medIndex) => 
                          renderMedication(med, medIndex)
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Aviso de auditoria */}
        {history.length > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700">
              <strong>Auditoria:</strong> Cada impressão registra o snapshot completo dos medicamentos. 
              Os dados são imutáveis e não podem ser alterados após o registro.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
